import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { CrmLead } from '@/types';

const COLLECTION = 'crmLeads';

// Real-time list of all CRM leads (admin only — enforced by Firestore rules).
export function useCrmLeads() {
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const q = query(collection(db, COLLECTION), orderBy('updatedAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setLeads(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as CrmLead[]);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching CRM leads:', err);
        setError(err as Error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  return { leads, loading, error };
}

// Mutations — thin wrappers around Firestore writes.
export type CrmLeadInput = Omit<CrmLead, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>;

// Firestore rejects undefined field values (ignoreUndefinedProperties is off),
// so drop any undefined keys before writing. Optional fields left blank in the
// form arrive as undefined and should simply be omitted.
function stripUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as Partial<T>;
}

export async function createLead(input: CrmLeadInput, createdBy: string) {
  return addDoc(collection(db, COLLECTION), {
    ...stripUndefined(input),
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateLead(id: string, input: Partial<CrmLeadInput>) {
  return updateDoc(doc(db, COLLECTION, id), {
    ...stripUndefined(input),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteLead(id: string) {
  return deleteDoc(doc(db, COLLECTION, id));
}
